import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { ChevronDown, ChevronsUpDown, ChevronUp, SquareMinus, SquarePlus } from 'lucide-react';

import DefaultTableResize from '../DefaultTableResize';
import { TruncateWithTooltipFormatter } from '../Grid/components/Table/formatters';

import { useRows, sorted } from './utils'

const propTypes = {
  columns: PropTypes.array,
  data: PropTypes.any,
  comparison: PropTypes.bool,
};

export default function HierarchicalTable({ columns: columnDefinition, data, onChange, comparison }) {
  const rows = useRows([], onChange)
  const [columns, setColumns] = useState([{}])
  const [sortOptions, setSortOptions] = useState({ order: null })

  const setDefaultData = useCallback(() => {
    rows.set(data && data.children.map((_, i) => ({ ..._, level: 0, index: `${i}`, __rowKey: `${i}` })) || [])
  }, [data])

  useEffect(() => {
    setDefaultData()
  }, [data])

  useEffect(() => {
    const Header = buildHeader()

    setColumns(columnDefinition.map((colDef, idx) => {
      if (idx === 0) colDef = expandFormat(colDef)

      if (colDef.customSort) {
        return {
          ...colDef,
          text: <Header definition={colDef} onSort={setSortOptions} />,
        }
      }
      return colDef
    }))
  }, [columnDefinition.map(({ dataField }) => dataField).join(), columnDefinition[0]?.text])

  const sort = array => {
    const { field, order } = sortOptions
    if (order) return sorted(array, typeof field === 'function' ? field : ({ [field]: f }) => f, order)
    return array
  }

  const hideChildren = (parent) => {
    if (!parent.expanded) return
    parent.children.forEach(child => {
      child.expanded && hideChildren(child)
      rows.remove(child)
    })
    parent.expanded = false
  }

  const showChildren = (parent, idx) => {
    const children = parent.children
    children.forEach((child, i) => {
      child.parent = parent
      child.index = `${parent.index}-${i}`
      child.__rowKey = `${parent.__rowKey}-${i}`
      child.level = parent.level + 1
    })
    rows.add(idx + 1, sort([...children]))
    parent.expanded = true
  }

  const handleExpandRow = (rowKey, rowIndex) => {
    const row = rows.find(r => r.__rowKey === rowKey)
    if (!row) return
    if (row.children) {
      if (row.expanded)
        hideChildren(row)
      else
        showChildren(row, rowIndex)
    }
  }

  useEffect(() => {
    if (sortOptions.order === 0) {
      setDefaultData()
      return
    }
    if (sortOptions.order) {
      rows.forEach(hideChildren)
      rows.set(sort(rows))
    }
  }, [sortOptions])

  const RowWrapper = ({ children, row, className }) => {
    const visibleIndex = typeof row?.index === 'number' ? row.index : undefined

    return <tr className={className} onClick={() => handleExpandRow(row?.__rowKey, visibleIndex)} >
      {children}
    </tr>
  }

  return <DefaultTableResize
    keyField='__rowKey'
    data={[...rows]}
    noDataIndication={() => 'No content to show.'}
    columns={columns}
    rowWrapper={RowWrapper}
    fixedFistColumnOnScroll
  />
}

HierarchicalTable.propTypes = propTypes
HierarchicalTable.defaultProps = {}

const expandFormat = ({ formatter, ...definition }) => ({
  ...definition,
  formatter(_, row) {
    return <>
      <div
        className='flex items-center gap-1 twy-text-base-accent cursor-pointer'
        style={{ paddingLeft: `${6 + row.level * 20}px` }}
      >
        {row.children && row.children.length ? <span className=''>
          {row.expanded ? <SquareMinus size='16' /> : <SquarePlus size='16' />}
        </span> : null}
        <TruncateWithTooltipFormatter
          value={formatter(_, row)}
          className='twy-text-base-accent hover:underline'
        />
      </div>
    </>
  },
})

const buildHeader = () => {
  let fieldSorted = null

  return ({ definition: { text, dataField, sortValue }, onSort }) => {
    const [sortedAsc, setSorted] = useState(null)

    useEffect(() => {
      if (fieldSorted && fieldSorted !== dataField) {
        setSorted(0)
      }
    }, [fieldSorted])

    const handleClick = useCallback(() => {
      let order;
      switch (sortedAsc) {
        case 1:
          order = 0
          break;

        case -1:
          order = 1
          break;

        default:
          order = -1
          break;
      }

      fieldSorted = dataField
      onSort({ field: sortValue || dataField, order })
      setSorted(order)
    }, [sortedAsc])

    let Icon = ChevronsUpDown

    switch (sortedAsc) {
      case -1:
        Icon = ChevronDown
        break;
      case 1:
        Icon = ChevronUp
        break;

      default: break;
    }

    return <div onClick={handleClick} className='flex items-center gap-1 capitalize'>
      {text}
      <Icon size={10} className='cursor-pointer twy-text-base-neutral hover:twy-text-base-accent' />
    </div>
  }
}
